
import React from 'react';
import { Bot, Star, Shield, Search, Filter, TrendingUp, Check, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PremiumBotCard } from '@/components/premium-bots/PremiumBotCard';
import { useNavigate } from 'react-router-dom';
import { usePremiumBots } from '@/hooks/use-premium-bots';
import { Skeleton } from '@/components/ui/skeleton';
import { TourButton } from '@/components/common/TourButton';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

const PremiumBots = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  
  const {
    premiumBots,
    isLoading,
    error,
    filters: {
      searchQuery,
      setSearchQuery,
      riskFilter,
      setRiskFilter,
      tagFilter,
      setTagFilter
    },
    helpers: {
      getRiskLabel,
      getRiskColor
    }
  } = usePremiumBots();
  
  const handleSubscribe = (botId: string, isIntegrated: boolean) => {
    navigate(`/marketplace/${botId}`);
  };
  
  if (isLoading) {
    return (
      <div className="space-y-6 md:space-y-8 pb-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-4">
          <div>
            <Skeleton className="h-8 md:h-10 w-48 md:w-64" />
            <Skeleton className="h-4 md:h-5 w-64 md:w-96 mt-2" />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-64 md:h-80 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 md:h-80 space-y-4">
        <Info className={`${isMobile ? "h-12 w-12" : "h-16 w-16"} text-destructive opacity-80`} />
        <h2 className={`${isMobile ? "text-xl" : "text-2xl"} font-medium`}>{t('no_bots_found_title')}</h2>
        <p className="text-sm md:text-base text-muted-foreground">{t('try_adjusting_search')}</p>
        <Button onClick={() => window.location.reload()} variant="outline">{t('refresh')}</Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6 md:space-y-8 pb-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-4" data-tour="premium-bots-header">
        <div>
          <h1 className={`${isMobile ? "text-xl" : "text-3xl"} font-bold`}>{t('premium_bots_title')}</h1>
          <p className={`${isMobile ? "text-sm" : "text-base"} text-muted-foreground mt-1`}>
            {t('premium_bots_subtitle')}
          </p>
        </div>
        <TourButton pageId="premium-bots" />
      </div>
      
      <div className="flex flex-col md:flex-row items-start md:items-center gap-4 flex-wrap" data-tour="premium-search-filter">
        <div className="relative w-full md:w-96">
          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground ${isMobile ? "h-4 w-4" : "h-[18px] w-[18px]"}`} />
          <Input 
            placeholder={t('search_bots')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className={`flex flex-wrap items-center gap-2 w-full md:w-auto ${isMobile ? "mt-1" : ""}`}>
          <div className="flex items-center gap-2 mr-2">
            <Filter size={isMobile ? 16 : 18} className="text-muted-foreground" />
            <span className={`${isMobile ? "text-xs" : "text-sm"} text-muted-foreground`}>{t('filter')}</span>
          </div>
          
          <Select value={riskFilter} onValueChange={setRiskFilter}>
            <SelectTrigger className={`w-[110px] md:w-[120px] ${isMobile ? "h-8 text-xs" : ""}`}>
              <SelectValue placeholder={t('risk_level')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all_risks')}</SelectItem>
              <SelectItem value="low">{t('low_risk')}</SelectItem>
              <SelectItem value="medium">{t('medium_risk')}</SelectItem>
              <SelectItem value="high">{t('high_risk')}</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger className={`w-[110px] md:w-[120px] ${isMobile ? "h-8 text-xs" : ""}`}>
              <SelectValue placeholder={t('tags')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all_tags')}</SelectItem>
              <SelectItem value="featured">{t('featured')}</SelectItem>
              <SelectItem value="bestseller">{t('bestseller')}</SelectItem>
              <SelectItem value="new">{t('newest')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <div className="flex justify-between items-center mb-4 md:mb-6">
          <h2 className={`${isMobile ? "text-base" : "text-lg"} font-medium`}>
            {t('bots_available', { count: premiumBots.length })} {t('bots_available_suffix')}
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {premiumBots.map((bot) => (
            <div data-tour="premium-stats-cards" key={bot.id}>
              <PremiumBotCard
                id={bot.id}
                name={bot.name}
                risk={getRiskLabel(bot.risk)}
                riskColor={getRiskColor(bot.risk)}
                description={bot.description}
                tradingStyle={bot.premiumDetails?.trading_style || "Unknown"}
                profitShare={bot.premiumDetails?.profit_share || 0}
                subscribersTotal={bot.premiumDetails?.subscribers_total || 0}
                connectedAccountsTotal={bot.premiumDetails?.connected_accounts_total || 0}
                minCapital={bot.premiumDetails?.min_capital || 0}
                isIntegrated={bot.premiumDetails?.is_integrated || false}
                isFeatured={bot.is_featured}
                isNew={bot.is_new}
                isBestSeller={bot.is_best_seller}
                exchanges={bot.exchange}
                colorScheme={bot.color_scheme}
                potentialProfit={bot.premiumDetails?.potential_profit}
                maxDrawdown={bot.premiumDetails?.max_drawdown}
                onActionClick={() => handleSubscribe(bot.id, bot.premiumDetails?.is_integrated || false)}
              />
              <div data-tour="premium-bot-action" className="hidden" />
            </div>
          ))}
        </div>
        
        {premiumBots.length === 0 && (
          <div className="text-center py-12 md:py-20">
            <Info className={`mx-auto ${isMobile ? "h-8 w-8" : "h-12 w-12"} text-muted-foreground opacity-30`} />
            <h3 className={`mt-3 md:mt-4 ${isMobile ? "text-base" : "text-lg"} font-medium`}>{t('no_bots_found_title')}</h3>
            <p className="mt-2 text-xs md:text-sm text-muted-foreground">
              {t('try_adjusting_search')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumBots;
